import {inject, injectable} from 'inversify'
import {DI} from '@/src/config/di/types'
import {OrderError} from '@/src/core/domain/errors'
import {OperationResult} from '@/src/shared/utils/result'
import type {OrderServiceInterface, OrderTotalsRequest} from '@/src/ports/services/order-service'
import type {IErrorReporter} from '@/src/ports/services/monitoring'
import type {OrderTotals, PersonTypeVariant} from '@/src/entities/order'

export type CalculateTotalsInput = {
  personType: PersonTypeVariant
  locationId: string
  deliveryServiceId?: string
  paymentServiceId?: string
  pickupPointId?: string
  bonusAmount?: number
  promoCode?: string
}

/**
 * Calculate Order Totals Use Case
 *
 * Orchestrates order totals calculation for preview.
 * Used to show estimated costs before order submission.
 *
 * @example
 * const result = await calculateTotalsUseCase.execute({
 *   personType: 'individual',
 *   locationId: '123',
 *   deliveryServiceId: 'cdek',
 * })
 * if (result.isOk()) {
 *   displayTotals(result.value)
 * }
 */
@injectable()
export class CalculateOrderTotalsUseCase {
  @inject(DI.IOrderService)
  orderService!: OrderServiceInterface

  @inject(DI.IErrorReporter)
  errorReporter!: IErrorReporter

  async execute(input: CalculateTotalsInput): Promise<OperationResult<OrderTotals, OrderError>> {
    const request: OrderTotalsRequest = {
      person_type: input.personType,
      location_id: input.locationId,
      delivery_service_id: input.deliveryServiceId,
      payment_service_id: input.paymentServiceId,
      pickup_point_id: input.pickupPointId,
      bonus_amount: input.bonusAmount,
      promo_code: input.promoCode,
    }

    // OrderService now returns Result, so we delegate error handling to it
    return this.orderService.getTotals(request)
  }
}
